import React, { useMemo, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useFamilyData } from "../../hooks/useFamilyData";
import { addStarterChores } from "../../utils/starterChores";

export default function SetupChecklistCard({ familyId, familySettings, setView }) {
  const { kids = [], tasks = [], history = [] } = useFamilyData(familyId);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState("");

  const { hasKids, hasTasks, hasApproval } = useMemo(() => {
    return {
      hasKids: kids.length > 0,
      hasTasks: tasks.length > 0,
      hasApproval: history.some((item) => item.type === "approve")
    };
  }, [kids, tasks, history]);

  const allDone = hasKids && hasTasks && hasApproval;
  const dismissed = familySettings?.setupChecklistDismissed;

  if (dismissed || allDone) return null;

  const goToKidsTab = () => {
    setView?.("settings");
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("tour:setSettingsSection", { detail: "settings" })
      );
      window.dispatchEvent(
        new CustomEvent("tour:setSettingsTab", { detail: "kids" })
      );
    }
  };

  const checklist = [
    {
      id: "kids",
      label: "Add at least one kid",
      done: hasKids,
      action: goToKidsTab
    },
    {
      id: "tasks",
      label: "Create a few chores",
      done: hasTasks,
      action: () => setView?.("parent")
    },
    {
      id: "approve",
      label: "Approve your first chore",
      done: hasApproval,
      action: () => setView?.("parent")
    }
  ];

  const handleStarterChores = async () => {
    if (!familyId || adding) return;
    setAdding(true);
    const count = await addStarterChores({ familyId, tasks });
    if (count > 0) {
      setNotice(`Added ${count} starter chores.`);
    } else {
      setNotice("Starter chores already added.");
    }
    setTimeout(() => setNotice(""), 2500);
    setAdding(false);
  };

  const handleDismiss = async () => {
    if (!familyId) return;
    await updateDoc(doc(db, "families", familyId), { setupChecklistDismissed: true });
  };

  return (
    <div className="glass parent-card setup-checklist">
      <div className="header-flex">
        <h2>Quick Start Checklist</h2>
        <button className="btn-nav" onClick={handleDismiss}>
          Hide
        </button>
      </div>
      <div className="card-body">
        <p style={{ opacity: 0.75 }}>
          Keep it simple at first. Complete these three steps to be fully set up.
        </p>
        <div className="setup-checklist-items">
          {checklist.map((item) => (
            <div key={item.id} className={`setup-checklist-item ${item.done ? "done" : ""}`}>
              <span>{item.done ? "✅" : "⬜"}</span>
              <span>{item.label}</span>
              {!item.done && (
                <button className="btn-nav" onClick={item.action}>Go</button>
              )}
            </div>
          ))}
        </div>
        {!hasTasks && (
          <button className="btn-nav success-btn" onClick={handleStarterChores} disabled={adding}>
            {adding ? "Adding starter chores..." : "Add Starter Chores"}
          </button>
        )}
        {notice && <p className="success-message">{notice}</p>}
        <button className="btn-nav" onClick={() => setView?.("parent")}>
          Go to Parent Hub
        </button>
      </div>
    </div>
  );
}
